import React from "react";

/**
 * Utility to automatically convert URLs in text to clickable hyperlinks
 */

// URL detection regex that matches:
// 1. http(s):// URLs
// 2. www. URLs
// 3. Bare domain names (e.g., google.com, example.co.uk)
const URL_REGEX =
  /(?:https?:\/\/(?:[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=%]+)|www\.(?:[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=%]+)|(?<![a-zA-Z0-9])([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:[/?#][a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=%]*)?)/gi;

/**
 * Ensure a URL has a protocol (http:// or https://)
 * This prevents the browser from treating bare domains as relative URLs
 *
 * @param url - The URL that may or may not have a protocol
 * @returns URL with protocol prepended if needed
 *
 * @example
 * ensureProtocol("google.com") // "http://google.com"
 * ensureProtocol("www.example.com") // "https://www.example.com"
 * ensureProtocol("https://example.com") // "https://example.com"
 */
export function ensureProtocol(url: string | null | undefined): string {
  if (!url || !url.trim()) {
    return "";
  }

  url = url.trim();

  // Already has protocol
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // www. prefix gets https
  if (url.startsWith("www.")) {
    return `https://${url}`;
  }

  // Bare domain or path gets http
  return `http://${url}`;
}

/**
 * Convert URLs in plain text to HTML anchor tags
 *
 * @param text - The text that may contain URLs
 * @returns HTML string with URLs converted to <a> tags
 *
 * @example
 * linkifyText("Visit https://example.com for more info")
 * // Returns: "Visit <a href="https://example.com" target="_blank" rel="noopener noreferrer">https://example.com</a> for more info"
 *
 * @example
 * linkifyText("Visit google.com for more info")
 * // Returns: "Visit <a href="http://google.com" target="_blank" rel="noopener noreferrer">google.com</a> for more info"
 */
export function linkifyText(text: string): string {
  if (!text) return text;

  return text.replace(URL_REGEX, (url) => {
    // Trim and validate URL
    url = url.trim();
    if (!url) return url;

    // Ensure URL has protocol
    let href: string;
    if (url.startsWith("http://") || url.startsWith("https://")) {
      href = url;
    } else if (url.startsWith("www.")) {
      href = `https://${url}`;
    } else {
      // Bare domain name - always prepend http://
      // Make absolutely sure we have a protocol to avoid relative URL issues
      href = `http://${url}`;
    }

    // Double-check href has protocol to prevent relative URL interpretation
    if (!href.startsWith("http://") && !href.startsWith("https://")) {
      href = `http://${href}`;
    }

    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline; word-break: break-all;">${url}</a>`;
  });
}

/**
 * Convert URLs in React text to clickable links (for JSX)
 *
 * @param text - The text that may contain URLs
 * @returns Array of React elements with links
 */
export function linkifyReactText(
  text: string
): (string | React.ReactElement)[] {
  if (!text) return [text];

  const parts: (string | React.ReactElement)[] = [];
  let lastIndex = 0;

  // Find all URLs in the text
  const regex = new RegExp(URL_REGEX);
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add the URL as a link
    let url = match[0];
    url = url.trim();

    let href: string;
    if (url.startsWith("http://") || url.startsWith("https://")) {
      href = url;
    } else if (url.startsWith("www.")) {
      href = `https://${url}`;
    } else {
      // Bare domain name - always prepend http://
      href = `http://${url}`;
    }

    // Double-check href has protocol to prevent relative URL interpretation
    if (!href.startsWith("http://") && !href.startsWith("https://")) {
      href = `http://${href}`;
    }

    parts.push(
      <a
        key={match.index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline break-all"
        onClick={(e) => e.stopPropagation()} // Prevent triggering parent click handlers
      >
        {url}
      </a>
    );

    lastIndex = regex.lastIndex;
  }

  // Add any remaining text after the last URL
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}
